import { useState, type ChangeEventHandler } from "react";
import { BsChevronDown } from "react-icons/bs";
import styles from "./MeritoSelect.module.scss";

let selectSequence = 0;

export interface MeritoSelectOption<T extends string = string> {
  value: T;
  label: string;
}

export interface MeritoSelectProps<T extends string = string> {
  value: T | "";
  options: MeritoSelectOption<T>[];
  onChange: (value: T) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  name?: string;
  id?: string;
  ariaLabel?: string;
}

export const MeritoSelect = <T extends string = string>({
  value,
  options,
  onChange,
  label,
  placeholder = "Select",
  error,
  disabled = false,
  name,
  id,
  ariaLabel,
}: MeritoSelectProps<T>): JSX.Element => {
  const [generatedId] = useState(() => `merito-select-${++selectSequence}`);
  const selectId = id ?? generatedId;
  const handleChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
    onChange(event.target.value as T);
  };

  return (
    <div className={styles.container}>
      {label ? <label className={styles.label} htmlFor={selectId}>{label}</label> : null}
      <div className={`${styles.box} ${error ? styles.error : ""} ${disabled ? styles.disabled : ""}`}>
        <select
          id={selectId}
          name={name}
          className={styles.select}
          value={value}
          disabled={disabled}
          aria-label={ariaLabel ?? label}
          aria-invalid={error ? true : undefined}
          onChange={handleChange}
        >
          {value ? null : <option value="" disabled>{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <BsChevronDown className={styles.chevron} aria-hidden="true" />
      </div>
      {error ? <span className={styles.errorMessage}>{error}</span> : null}
    </div>
  );
};

export default MeritoSelect;
